import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Tog, Textarea } from '../ui.jsx';

const TRIGGERS = ['New Lead','Missed Call','Stage: Qualified','Stage: Booked','Estimate Sent','Invoice Paid','Tag Added','Manual','90 Days Inactive'];
const STEP_TYPES = [
  { v:'text', label:'📱 SMS', color:G.cyan },
  { v:'email', label:'📧 Email', color:G.purple },
  { v:'ai_call', label:'🤖 AI Call', color:G.orange },
  { v:'wait', label:'⏳ Wait', color:G.textMuted },
  { v:'stage', label:'🔀 Move Stage', color:G.green },
  { v:'notify', label:'🔔 Notify Team', color:G.yellow },
];

export function Workflows({ toast }) {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);

  // Always re-fetch on tab focus + every mount so a failed first load doesn't
  // strand the screen with a blank grid forcing a hard refresh.
  const load = () => {
    setLoading(true);
    setError(null);
    return api('/api/workflows')
      .then(data => { setWorkflows(Array.isArray(data) ? data : []); })
      .catch(e => { setError(e.message); toast(e.message, 'error'); })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener('tradeops:tab-focus', onFocus);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) load(); });
    return () => window.removeEventListener('tradeops:tab-focus', onFocus);
  }, []);

  const toggle = async w => {
    const next = w.status === 'active' ? 'paused' : 'active';
    try { await api(`/api/workflows/${w.id}`, { method:'PATCH', body:{ status: next } }); load(); toast(`Workflow ${next}`); }
    catch(e) { toast(e.message,'error'); }
  };

  const remove = async w => {
    if (w.protected) { toast('Cannot delete protected workflows','warn'); return; }
    if (!confirm(`Delete "${w.name}"?`)) return;
    try { await api(`/api/workflows/${w.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Workflows & Follow-Ups</h1>
      <Btn v="primary" onClick={()=>setEditing({ name:'New Sequence', trigger:'New Lead', status:'paused', steps:[] })}>+ New Workflow</Btn>
    </div>

    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.6 }}>
      Workflows fire automatically based on a trigger event (new lead, estimate sent, invoice paid, etc.) and run a sequence of SMS, email, AI calls, and waits.
      The "Estimate Sent" workflow is what fires when you click <strong style={{ color:G.text }}>Send</strong> on an estimate — it does the follow-up touches that close more deals.
    </div>

    {error && !loading && (
      <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.red+'66', background:G.red+'18', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10 }}>
        <div style={{ fontSize:13 }}>⚠️ Couldn't load workflows: {error}</div>
        <Btn sz="sm" v="primary" onClick={load}>Retry</Btn>
      </div>
    )}
    {!loading && !error && workflows.length === 0 && (
      <div style={{ ...styles.card, padding:24, textAlign:'center', color:G.textMuted, fontSize:13, marginBottom:14 }}>
        No workflows yet. Click <strong style={{ color:G.text }}>+ New Workflow</strong> to create your first sequence.
        <div style={{ marginTop:10 }}><Btn sz="sm" onClick={load}>↻ Refresh</Btn></div>
      </div>
    )}
    {loading ? <Spinner/> : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(360px,1fr))', gap:14 }}>
      {workflows.map(w => (
        <div key={w.id} style={styles.card}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <strong style={{ fontSize:15 }}>{w.name}</strong>
                {w.protected && <Badge c={G.purple} bg={G.purpleDim}>Protected</Badge>}
              </div>
              <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Trigger: <strong style={{ color:G.text }}>{w.trigger}</strong></div>
              <div style={{ fontSize:13, color:G.textMuted }}>{w.steps?.length || 0} steps · {w.runs} runs</div>
            </div>
            <Tog on={w.status === 'active'} onChange={()=>toggle(w)} />
          </div>
          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            {(w.steps||[]).slice(0,5).map((s,i) => {
              const t = STEP_TYPES.find(x=>x.v===s.type) || STEP_TYPES[0];
              return <Badge key={i} c={t.color} bg={t.color+'22'}>{t.label}</Badge>;
            })}
            {(w.steps||[]).length > 5 && <Badge c={G.textMuted} bg={G.input}>+{w.steps.length-5}</Badge>}
          </div>
          <div style={{ display:'flex', gap:6, marginTop:12 }}>
            <Btn sz="sm" onClick={()=>setEditing(w)}>Edit</Btn>
            {!w.protected && <Btn sz="sm" v="danger" onClick={()=>remove(w)}>Delete</Btn>}
          </div>
        </div>
      ))}
    </div>}

    <WorkflowModal workflow={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
  </div>;
}

function WorkflowModal({ workflow, onClose, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(workflow || {}); }, [workflow]);
  if (!workflow) return null;
  const isNew = !form.id;
  const steps = form.steps || [];

  const updateStep = (i, k, v) => {
    const ns = steps.slice();
    ns[i] = { ...ns[i], [k]: v };
    setForm({ ...form, steps: ns });
  };
  const addStep = type => setForm({ ...form, steps: [...steps, { id:`s${Date.now()}`, type, content:'', delay: type==='wait' ? '1 day' : 'immediate' }] });
  const delStep = i => setForm({ ...form, steps: steps.filter((_,j)=>j!==i) });
  const moveStep = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const ns = steps.slice();
    [ns[i], ns[j]] = [ns[j], ns[i]];
    setForm({ ...form, steps: ns });
  };

  const save = async () => {
    try {
      if (isNew) await api('/api/workflows', { method:'POST', body: form });
      else await api(`/api/workflows/${form.id}`, { method:'PATCH', body: form });
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={760}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Workflow':form.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:14 }}>
      <Field label="Name"><Input value={form.name||''} onChange={e=>setForm({...form,name:e.target.value})}/></Field>
      <Field label="Trigger">
        <Select value={form.trigger||'New Lead'} onChange={e=>setForm({...form,trigger:e.target.value})}>
          {TRIGGERS.map(t => <option key={t}>{t}</option>)}
        </Select>
      </Field>
      <Field label="Status">
        <Select value={form.status||'paused'} onChange={e=>setForm({...form,status:e.target.value})}>
          <option value="paused">Paused</option>
          <option value="active">Active</option>
        </Select>
      </Field>
    </div>

    <div style={{ marginTop:8 }}>
      <div style={{ fontSize:12, fontWeight:600, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Sequence Steps</div>
      {steps.map((s, i) => {
        const t = STEP_TYPES.find(x=>x.v===s.type) || STEP_TYPES[0];
        return <div key={s.id||i} style={{ ...styles.card, padding:12, marginBottom:8, borderColor: t.color+'44' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <Badge c={t.color} bg={t.color+'22'}>Step {i+1} · {t.label}</Badge>
            <div style={{ flex:1 }}/>
            <Btn sz="xs" onClick={()=>moveStep(i,-1)}>↑</Btn>
            <Btn sz="xs" onClick={()=>moveStep(i,1)}>↓</Btn>
            <Btn sz="xs" v="danger" onClick={()=>delStep(i)}>×</Btn>
          </div>
          <div style={{ display:'grid', gridTemplateColumns: s.type==='wait' ? '1fr' : '1fr 200px', gap:8 }}>
            {s.type !== 'wait' && <Textarea placeholder={s.type==='text' ? 'Hi {{first_name}}, ...' : s.type==='email' ? 'Email body' : s.type==='stage' ? 'Stage name (e.g. Qualified)' : s.type==='notify' ? 'Notification message' : 'Script reference (e.g. estimate_follow_up)'}
              value={s.content||''} onChange={e=>updateStep(i,'content',e.target.value)} style={{ minHeight:54 }}/>}
            <Input placeholder="Delay (e.g. 1 day, 30 minutes, immediate)" value={s.delay||''} onChange={e=>updateStep(i,'delay',e.target.value)} />
          </div>
        </div>;
      })}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
        {STEP_TYPES.map(t => <Btn key={t.v} sz="sm" onClick={()=>addStep(t.v)}>+ {t.label}</Btn>)}
      </div>
    </div>

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:18 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}
